import { useEffect, useState } from 'react';

import {
  RemoveAssignmentEmploymentToAppointmentGroupingRequest,
  TransportationMode
} from 'generated';
import { useTranslation } from 'react-i18next';

import { Event } from '../../types';

import { mutations } from 'RestAPIs/rapini';

import AssignEmployeeDialog from '../AssignEmployeeDialog';
import { ContextMenu } from '../ContextMenu';
import EditRouteDialog from '../EditRouteDialog/EditRouteDialog';
import { SxProps } from '@mui/material';
import { MAPVIEW_SESSION_STORAGE_KEYS } from '../../AzureMaps/types';
import { useSessionStorage } from 'usehooks-ts';
import { ContextMenuOptions } from '../ContextMenu/types';
import { useRecoilState } from 'recoil';
import { planResponsePlanningEntityIdentifiersAtom } from '../../atoms';
import { refetchPlanResponsePlanningEntityIdentifiers } from '../../utils';
import { showSuccessSnackbar } from 'components/shared/Snackbar';

enum DialogId {
  CONNECT_EMPLOYEE = 'connectEmployee',
  EDIT_ROUTE = 'editRoute'
}

type Props = {
  appointmentGroupingEntityId: string;
  appointmentGroupingStart: string;
  appointmentGroupingName?: string;
  transportationMode?: TransportationMode;
  onConnectClick?: () => void;
  onDialogOpen?: (isOpen: boolean) => void;
  events?: Event[];
  employeeId?: number | null;
  onOpen?: (open: boolean) => void;
  onChangeMousePosition?: (x: number, y: number) => void;
  clickType?: 'lmb' | 'rmb';
  scheduleId: string;
  sx?: SxProps;
  menuSx?: SxProps;
  isAssignableToEmployee: boolean;
  optionsToHide?: ContextMenuOptions[];
};

export default function RouteContextMenu({
  appointmentGroupingEntityId,
  appointmentGroupingName,
  appointmentGroupingStart,
  clickType,
  events,
  employeeId,
  isAssignableToEmployee,
  menuSx,
  onConnectClick,
  onDialogOpen,
  onOpen,
  onChangeMousePosition,
  transportationMode,
  scheduleId,
  sx,
  optionsToHide = []
}: Props) {
  const { t } = useTranslation('common', {
    keyPrefix: 'activityPlanning.routeContextMenu'
  });
  const firstAppointment = events ? events[0] : undefined;
  const [activeDialogId, setActiveDialogId] = useState<DialogId | null>(null);
  const [, setFilteredSchedule] = useSessionStorage<{
    start: string;
    scheduleId: string;
  }>(MAPVIEW_SESSION_STORAGE_KEYS.FILTERED_SCHEDULE, {
    start: '',
    scheduleId: ''
  });
  const [, setPlanResponsePlanningEntityIdentifiers] = useRecoilState(
    planResponsePlanningEntityIdentifiersAtom
  );

  const { mutate } = mutations.useUpdateAppointmentGroupingTransportationMode(
    appointmentGroupingEntityId
  );

  const { mutate: removeAssignmentEmploymentToAppointmentGrouping } =
    mutations.useRemoveAssignmentEmploymentToAppointmentGrouping(
      appointmentGroupingEntityId
    );

  const options = [
    ContextMenuOptions.CONNECT_ROUTE,
    ...(firstAppointment ? [ContextMenuOptions.EDIT_ROUTE_START] : []),
    ...(transportationMode
      ? transportationMode === 'CAR'
        ? [ContextMenuOptions.CHANGE_TO_BICYCLE]
        : [ContextMenuOptions.CHANGE_TO_CAR]
      : []),
    ...(isAssignableToEmployee ? [ContextMenuOptions.CONNECT_EMPLOYEE] : []),
    ...(isAssignableToEmployee && employeeId
      ? [
          ContextMenuOptions.REMOVE_ASSIGNMENT_EMPLOYMENT_TO_APPOINTMENT_GROUPING
        ]
      : []),
    ContextMenuOptions.SHOW_ROUTE_ON_MAP
  ]
    .filter((option) => !optionsToHide.includes(option as ContextMenuOptions))
    .map((option) => t(option));

  function onContextMenuItemClick(option: string) {
    switch (option) {
      case t(ContextMenuOptions.CONNECT_ROUTE):
        onConnectClick?.();
        break;
      case t(ContextMenuOptions.CHANGE_TO_BICYCLE):
        mutate(
          { transportationMode: 'BICYCLE' },
          {
            onSuccess: () => {
              showSuccessSnackbar();
              refetchPlanResponsePlanningEntityIdentifiers(
                setPlanResponsePlanningEntityIdentifiers
              );
            }
          }
        );
        break;
      case t(ContextMenuOptions.CHANGE_TO_CAR):
        mutate(
          { transportationMode: 'CAR' },
          {
            onSuccess: () => {
              showSuccessSnackbar();
              refetchPlanResponsePlanningEntityIdentifiers(
                setPlanResponsePlanningEntityIdentifiers
              );
            }
          }
        );
        break;
      case t(ContextMenuOptions.CONNECT_EMPLOYEE):
        setActiveDialogId(DialogId.CONNECT_EMPLOYEE);
        break;
      case t(ContextMenuOptions.EDIT_ROUTE_START):
        setActiveDialogId(DialogId.EDIT_ROUTE);
        break;
      case t(ContextMenuOptions.SHOW_ROUTE_ON_MAP):
        onShowRouteOnMap();
        break;
      case t(
        ContextMenuOptions.REMOVE_ASSIGNMENT_EMPLOYMENT_TO_APPOINTMENT_GROUPING
      ): {
        const payload: RemoveAssignmentEmploymentToAppointmentGroupingRequest =
          {
            date: appointmentGroupingStart
          };
        removeAssignmentEmploymentToAppointmentGrouping(payload, {
          onSuccess: () => {
            showSuccessSnackbar();
            refetchPlanResponsePlanningEntityIdentifiers(
              setPlanResponsePlanningEntityIdentifiers
            );
          }
        });
        break;
      }
    }
  }

  function handleDialogClose() {
    setActiveDialogId(null);
  }

  function onShowRouteOnMap() {
    setFilteredSchedule({
      start: appointmentGroupingStart,
      scheduleId
    });
    window.open('/#roster,activityPlanning&view=map', '_self');
  }

  useEffect(() => {
    onDialogOpen?.(!!activeDialogId);

    return () => {
      onDialogOpen?.(false);
    };
  }, [activeDialogId]);

  return (
    <>
      <ContextMenu
        options={options}
        onOptionClick={onContextMenuItemClick}
        onOpen={(open, event) => {
          onOpen?.(open);
          onChangeMousePosition?.(event?.clientX || 0, event?.clientY || 0);
        }}
        clickType={clickType}
        sx={sx}
        menuSx={menuSx}
      />
      {activeDialogId === DialogId.CONNECT_EMPLOYEE && (
        <AssignEmployeeDialog
          open={activeDialogId === DialogId.CONNECT_EMPLOYEE}
          handleClose={handleDialogClose}
          appointmentGroupingEntityId={appointmentGroupingEntityId}
          date={appointmentGroupingStart}
          appointmentGroupingName={appointmentGroupingName ?? ''}
        />
      )}
      {activeDialogId === DialogId.EDIT_ROUTE &&
        events?.length &&
        events.find(
          ({ category }) => category === 'NON_CLIENT' || category === 'CLIENT'
        ) && (
          <EditRouteDialog
            appointmentGroupingEntityId={appointmentGroupingEntityId}
            appointmentGroupingStart={appointmentGroupingStart}
            open={activeDialogId === DialogId.EDIT_ROUTE}
            events={events}
            handleClose={handleDialogClose}
          />
        )}
    </>
  );
}
